import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import core from '@gin-betting/core';

import { useWidgetSettings, useCssClassToggleOnDocument } from 'hooks';
import { useAsyncModule } from 'async-modules';
import {
    isMobile,
    SELECTOR_STUB,
    BettingWidgets,
    CLASS_NAME_OF_DISABLED_SCROLL,
} from 'app-constants';
import { State, Modules } from 'types';
import ui from 'modules/ui';

const {
    utils: { getBettingOptions },
    viewport: {
        selectors: { selectIsMedium },
    },
} = core;

const {
    selectors: { selectIsSidebarOpen },
    actions: { toggleSidebar, openSidebar, closeSidebar },
} = ui;

interface Output {
    handleToggleSidebar: VoidFn;
    handleCloseSidebar: VoidFn;
    handleOpenSidebar: VoidFn;
    isSidebarOpen: boolean;
    isFullScreen: boolean;
    isMedium: boolean;
    isAuthorized: boolean;
    ignoreOffsetInTheaterMode: boolean;
}

const makePropsSelector = (
    streamModule: Modules['stream']['default'] | null
) => {
    const selectIsFullScreen =
        streamModule?.selectors.selectIsFullScreen ?? SELECTOR_STUB;

    return createStructuredSelector({
        isSidebarOpen: selectIsSidebarOpen,
        isFullScreen: selectIsFullScreen,
        isMedium: selectIsMedium,
    });
};

const useSidebarToggler = (): Output => {
    const streamModule = useAsyncModule('stream');
    const { ignoreOffsetInTheaterMode } = useWidgetSettings({
        widget: BettingWidgets.STREAM,
    });
    const dispatch = useDispatch();
    const selectProps = useMemo(() => makePropsSelector(streamModule), [
        streamModule,
    ]);

    const { isSidebarOpen, isFullScreen, isMedium } = useSelector((s: State) =>
        selectProps(s, {})
    );

    useCssClassToggleOnDocument(
        CLASS_NAME_OF_DISABLED_SCROLL,
        isSidebarOpen && isMobile
    );

    const handleToggleSidebar = useCallback(() => {
        dispatch(toggleSidebar({ isOpen: !isSidebarOpen }));
    }, [dispatch, isSidebarOpen]);

    const handleCloseSidebar = useCallback(() => {
        dispatch(closeSidebar());
    }, [dispatch]);

    const handleOpenSidebar = useCallback(() => {
        dispatch(openSidebar());
    }, [dispatch]);

    const { isAuthorized } = getBettingOptions();

    return {
        handleToggleSidebar,
        handleCloseSidebar,
        handleOpenSidebar,
        isSidebarOpen,
        isFullScreen: !!isFullScreen,
        isMedium,
        isAuthorized,
        ignoreOffsetInTheaterMode: Boolean(ignoreOffsetInTheaterMode),
    };
};

export default useSidebarToggler;
